(() => {
  'use strict';
  if (window.ARMATEQCart) return;
  const configNode = document.getElementById('nm-cart-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const labels = config.labels;
  const root = `${config.root.replace(/\/$/, '')}/`;
  let drawer = document.querySelector('[data-nm-cart-dialog]');
  let cart = null;
  let opener = null;
  let previousBodyOverflow = '';
  let busyCount = 0;
  let requestVersion = 0;
  let queue = Promise.resolve();
  const pending = new Map();
  const products = new Map();
  let mutationFocus = null;

  const surfaces = () => [...document.querySelectorAll('[data-nm-cart-surface]')];
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  };
  const safeUrl = (value, fallback = config.shopUrl, sameOrigin = false) => {
    try {
      const url = new URL(value, location.origin);
      if (!['http:', 'https:'].includes(url.protocol)) return fallback;
      if (sameOrigin && url.origin !== location.origin) return fallback;
      return url.href;
    } catch { return fallback; }
  };
  const money = (minor, withCurrency = false) => {
    const currency = cart?.currency || config.currency;
    try {
      const formatted = new Intl.NumberFormat(config.locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' }).format(Number(minor || 0) / 100);
      return withCurrency ? `${formatted} ${currency}` : formatted;
    } catch { return `${(Number(minor || 0) / 100).toFixed(2)} ${currency}`; }
  };
  const button = (text, className, action, key) => {
    const node = element('button', className, text);
    node.type = 'button';
    node.dataset.nmCartAction = action;
    if (key) node.dataset.key = key;
    return node;
  };
  const showMessage = (message = '', error = false) => {
    surfaces().forEach(surface => {
      const node = surface.querySelector(error ? '[data-nm-cart-error]' : '[data-nm-cart-status]');
      if (!node) return;
      node.textContent = message;
      node.hidden = !message;
    });
  };
  const clearMessages = () => { showMessage(); showMessage('', true); };
  const captureFocus = () => {
    const focused = document.activeElement;
    const line = focused?.closest('[data-nm-cart-line]');
    const surface = focused?.closest('[data-nm-cart-surface]');
    if (!line || !surface) return null;
    return { surface, key: line.dataset.key, variant: line.dataset.variantId, index: [...surface.querySelectorAll('[data-nm-cart-line]')].indexOf(line), action: focused.dataset.nmCartAction, quantity: focused.hasAttribute('data-nm-cart-quantity') };
  };
  const restoreFocus = plan => {
    if (!plan?.surface?.isConnected) return;
    const lines = [...plan.surface.querySelectorAll('[data-nm-cart-line]')];
    const line = lines.find(node => node.dataset.key === plan.key) || lines.find(node => node.dataset.variantId === plan.variant) || lines[Math.min(plan.index, lines.length - 1)];
    const target = plan.quantity ? line?.querySelector('[data-nm-cart-quantity]') : [...(line?.querySelectorAll('[data-nm-cart-action]') || [])].find(control => control.dataset.nmCartAction === plan.action);
    (target || line?.querySelector('[data-nm-cart-action="edit"], [data-nm-cart-quantity]') || plan.surface.querySelector('[data-nm-cart-close], [data-nm-cart-checkout], .nm-cart-shop'))?.focus({ preventScroll: true });
  };
  const setBusy = () => {
    const busy = busyCount > 0;
    surfaces().forEach(surface => {
      surface.setAttribute('aria-busy', String(busy));
      surface.querySelectorAll('[data-nm-cart-action], [data-nm-cart-checkout], [data-nm-cart-quantity], [data-nm-cart-edit-select]').forEach(control => {
        control.disabled = busy || control.dataset.unavailable === 'true';
      });
    });
  };
  const api = async (path, options = {}) => {
    let response;
    try {
      response = await fetch(new URL(`${root}${path}`, location.origin).href, {
        credentials: 'same-origin',
        cache: 'no-store',
        ...options,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest', ...(options.headers || {}) }
      });
    } catch (cause) {
      const error = new Error(labels.networkError || labels.error, { cause });
      error.code = 'CART_NETWORK_ERROR';
      throw error;
    }
    let result;
    try { result = await response.json(); } catch { throw new Error(labels.error); }
    if (!response.ok || result.status >= 400) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : labels.error);
    return result;
  };
  const post = (path, body) => api(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

  const offerSettings = config.bundleOffer || {};
  let bundleSdkPromise;
  const loadBundleSdk = () => {
    if (customElements.get('kaching-bundle-component')) return Promise.resolve();
    if (!bundleSdkPromise) bundleSdkPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = offerSettings.sdkUrl;
      script.onload = () => {
        if (customElements.get('kaching-bundle-component')) resolve();
        else { script.remove(); bundleSdkPromise = null; reject(new Error(labels.offerUnavailable)); }
      };
      script.onerror = () => { script.remove(); bundleSdkPromise = null; reject(new Error(labels.offerUnavailable)); };
      document.head.append(script);
    });
    return bundleSdkPromise;
  };
  const shirtIds = new Set((offerSettings.shirtProductIds || []).map(Number));
  const breechIds = new Set((offerSettings.breechProductIds || []).map(Number));
  const breechShirtIds = new Set((offerSettings.breechShirtProductIds || offerSettings.shirtProductIds || []).map(Number));
  let offerState = null;
  let offerVersion = 0;
  let offerChooser = null;
  const releaseOfferChooser = (restoreFocus = true) => {
    if (!offerChooser) return;
    const previous = offerChooser;
    offerChooser = null;
    previous.shell?.removeAttribute('inert');
    if (restoreFocus && drawer?.open) {
      const target = previous.opener?.isConnected ? previous.opener : offerState?.add?.isConnected && !offerState.add.disabled ? offerState.add : offerState?.details.querySelector('summary');
      target?.focus({ preventScroll: true });
    }
  };
  const closeOfferChooser = (restoreFocus = true) => {
    if (!offerChooser) return;
    const previous = offerChooser;
    previous.closeButton?.click();
    releaseOfferChooser(restoreFocus);
  };
  // Kaching renders its chooser in body. Keep that existing node in the native
  // dialog's top layer, retaining the vendor's selection and dismissal handlers.
  const observeOfferChooser = () => {
    if (offerChooser) {
      if (!offerChooser.modal.isConnected || !offerChooser.host.isConnected) releaseOfferChooser();
      else return;
    }
    if (!drawer?.open || !offerState?.details.open) return;
    const modal = [...document.querySelectorAll('.kaching-bundles__choose-product-modal')].find(node => !node.closest('[data-nm-cart-dialog]'));
    if (!modal) return;
    let host = modal;
    while (host.parentElement && host.parentElement !== document.body) host = host.parentElement;
    if (host.parentElement !== document.body || host === drawer) return;
    const heading = modal.querySelector('h2');
    const closeButton = modal.querySelector('.kaching-bundles__choose-product__close, button[aria-label="Close"]') || heading?.parentElement.querySelector('button, [role="button"]');
    if (!closeButton) return;
    const shell = drawer.querySelector('.nm-cart-shell');
    const trigger = offerState.mount.querySelector('.kaching-bundles__bar--selected .kaching-bundles__collection-product__choose-product-button') || offerState.details.querySelector('summary');
    offerChooser = { host, modal, closeButton, shell, opener: trigger };
    host.setAttribute('data-nm-cart-chooser', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    if (heading) { heading.id = 'NmCartChooseProduct'; modal.setAttribute('aria-labelledby', heading.id); }
    drawer.append(host);
    shell?.setAttribute('inert', '');
    closeButton.focus({ preventScroll: true });
  };
  const chooserObserver = new MutationObserver(observeOfferChooser);
  chooserObserver.observe(document.body, { childList: true, subtree: true });
  drawer?.addEventListener('keydown', event => {
    if (event.key === 'Escape' && offerChooser) {
      event.preventDefault();
      event.stopPropagation();
      closeOfferChooser();
    }
  }, true);
  drawer?.addEventListener('cancel', event => {
    if (offerChooser) { event.preventDefault(); closeOfferChooser(); }
  });
  const knownBundleDeals = [
    { ids: new Set([offerSettings.shirtDealId, ...(offerSettings.shirtDealAliases || [])].filter(Boolean)), singleBars: new Set(offerSettings.shirtSingleBarIds || []), mainProducts: shirtIds },
    { ids: new Set([offerSettings.breechDealId, ...(offerSettings.breechDealAliases || [])].filter(Boolean)), singleBars: new Set(offerSettings.breechSingleBarIds || []), mainProducts: breechIds }
  ];
  const readBundleMetadata = item => {
    const raw = item.properties?.__kaching_bundles;
    if (typeof raw !== 'string') return null;
    try {
      const metadata = JSON.parse(raw);
      return metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : null;
    } catch { return null; }
  };
  const hasReusableBundleMetadata = item => {
    const bundleKeys = Object.keys(item.properties || {}).filter(key => /^_.*(?:kaching|bundle)/i.test(key));
    if (!bundleKeys.length) return true;
    if (bundleKeys.length !== 1 || bundleKeys[0] !== '__kaching_bundles') return false;
    const metadata = readBundleMetadata(item);
    const deal = metadata && knownBundleDeals.find(candidate => candidate.ids.has(metadata.deal));
    if (!deal) return false;
    // Reuse only ordinary main/collection lines from our configured deals.
    // Gift, upsell, BXGY, subscription and unknown payload fields stay excluded.
    const allowed = new Set(['deal', 'pp', 'main', 'id', 'bar', 'bid', 'collectionBreaksProduct', 'market', 'v', 'abid', 'ab']);
    if (Object.keys(metadata).some(key => !allowed.has(key))) return false;
    const main = metadata.main === true && metadata.collectionBreaksProduct == null;
    const collection = metadata.collectionBreaksProduct === true && metadata.main == null;
    if (!main && !collection) return false;
    if (main && (!deal.mainProducts.has(Number(item.product_id)) || Number(metadata.pp) !== Number(item.product_id))) return false;
    if (collection && !breechShirtIds.has(Number(item.product_id))) return false;
    const bar = metadata.bar || metadata.bid;
    if (typeof bar !== 'string' || !bar || (metadata.bar && metadata.bid && metadata.bar !== metadata.bid)) return false;
    // A one-item bar is a standalone purchase, even when Kaching tagged it.
    if (main && deal.singleBars.has(bar) && metadata.id == null) return true;
    if (typeof metadata.id !== 'string' || !metadata.id) return false;
    // Removing one half of a pair leaves its metadata behind. Only that orphan
    // can be offered another partner; an existing group must remain intact.
    return !cart.items.some(other => {
      if (other.key === item.key || other.quantity <= 0) return false;
      const partner = readBundleMetadata(other);
      return partner && deal.ids.has(partner.deal) && partner.id === metadata.id;
    });
  };
  const offerEligible = item => item.quantity === 1 && item.requires_shipping && !item.selling_plan_allocation &&
    Number(item.original_line_price) === Number(item.final_line_price) && !(item.line_level_discount_allocations || []).length &&
    hasReusableBundleMetadata(item) &&
    (shirtIds.has(Number(item.product_id)) || breechIds.has(Number(item.product_id)));
  const getProduct = async item => {
    const productUrl = new URL(safeUrl(item.url, config.shopUrl, true));
    productUrl.search = '';
    productUrl.hash = '';
    productUrl.pathname += '.js';
    let product = products.get(productUrl.href);
    if (!product) {
      const response = await fetch(productUrl.href, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(labels.error);
      product = await response.json();
      products.set(productUrl.href, product);
    }
    return product;
  };
  const widgetProducts = widget => {
    const found = [];
    ['product', 'other-products', 'collection-breaks-products'].forEach(attribute => {
      try {
        const value = JSON.parse(widget.getAttribute(attribute) || 'null');
        if (Array.isArray(value)) found.push(...value);
        else if (value) found.push(value);
      } catch {}
    });
    return found;
  };
  const offerSelection = state => {
    const widget = state.mount.querySelector('kaching-bundles-block');
    if (!widget || typeof widget.items !== 'function') return { message: labels.offerLoading };
    let deal;
    try { deal = JSON.parse(widget.getAttribute('deal-block') || '{}'); } catch { return { message: labels.offerUnavailable }; }
    const expectedDeal = breechIds.has(Number(state.source.product_id)) ? offerSettings.breechDealId : offerSettings.shirtDealId;
    const verifiedOffer = deal.id === expectedDeal && deal.dealBars?.some(bar => Number(bar.quantity) === 2 && bar.dealBarType === 'quantity-break' && bar.discountType === 'percentage' && Number(bar.discountValue) === 20 && !bar.showAsSoldOutEnabled);
    if (!verifiedOffer) return { message: labels.offerUnavailable };
    const unfinished = [...widget.querySelectorAll('.kaching-bundles__bar--selected .kaching-bundles__collection-product__choose-product-button, .kaching-bundles__bar--selected .kaching-bundles__collection-product__choose-product-image')].some(control => control.getClientRects().length > 0);
    if (unfinished) return { message: labels.offerChoose };
    let selected;
    try { selected = widget.items(); } catch { return { message: labels.offerChoose }; }
    if (!Array.isArray(selected) || !selected.length) return { message: labels.offerChoose };
    const items = selected.map(item => ({ ...item, id: Number(item.id), quantity: Number(item.quantity || 1), properties: { ...(item.properties || {}) } }));
    if (items.some(item => !Number.isSafeInteger(item.id) || !Number.isInteger(item.quantity) || item.quantity < 1 || item.selling_plan || item.parent_id || item.parent_relationship || item.components)) return { message: labels.offerChoose };
    if (items.reduce((sum, item) => sum + item.quantity, 0) !== 2) return { message: labels.offerChoose };
    const originalId = Number(state.source.variant_id || state.source.id);
    const matching = items.find(item => item.id === originalId);
    if (!matching) return { message: labels.offerKeepItem };
    if (!Object.keys(matching.properties).some(key => /^_.*kaching/i.test(key))) return { message: labels.offerChoose };
    const extras = items.map(item => ({ ...item }));
    const originalIndex = extras.findIndex(item => item.id === originalId);
    extras[originalIndex].quantity -= 1;
    const added = extras.filter(item => item.quantity > 0);
    if (added.length !== 1 || added[0].quantity !== 1) return { message: labels.offerChoose };
    const allowedShirts = breechIds.has(Number(state.source.product_id)) ? breechShirtIds : shirtIds;
    const availableShirts = widgetProducts(widget).filter(product => allowedShirts.has(Number(product.id))).flatMap(product => product.variants || []);
    const shirt = availableShirts.find(variant => Number(variant.id) === added[0].id);
    if (!shirt || shirt.availableForSale === false || shirt.available === false || Number(shirt.compareAtPrice || shirt.compare_at_price || 0) > Number(shirt.price || 0)) return { message: labels.offerEligibleShirt };
    return { original: matching, added: added[0], widget };
  };
  const resetOffer = () => {
    ++offerVersion;
    closeOfferChooser(false);
    offerState?.observer?.disconnect();
    clearTimeout(offerState?.timer);
    offerState = null;
  };
  const renderOffer = async node => {
    if (!node.closest('[data-nm-cart-dialog]') || !shirtIds.size) { node.hidden = true; return; }
    const candidates = cart.items.filter(offerEligible).sort((left, right) => Number(breechIds.has(Number(right.product_id))) - Number(breechIds.has(Number(left.product_id))));
    if (!candidates.length) { resetOffer(); node.hidden = true; node.replaceChildren(); return; }
    if (offerState?.node === node && node.isConnected && candidates.some(item => item.key === offerState.source.key)) return;
    resetOffer();
    const version = offerVersion;
    node.hidden = true;
    node.replaceChildren();
    let source;
    for (const candidate of candidates) {
      let product;
      try { product = await getProduct(candidate); } catch {
        if (version !== offerVersion || !node.isConnected) return;
        continue;
      }
      if (version !== offerVersion || !node.isConnected) return;
      const current = cart.items.find(item => item.key === candidate.key && offerEligible(item));
      if (!current) continue;
      const variant = product.variants?.find(value => Number(value.id) === Number(current.variant_id || current.id));
      if (!variant?.available || Number(variant.compare_at_price || 0) > Number(variant.price)) continue;
      source = current;
      break;
    }
    if (!source) return;
    const details = element('details', 'nm-cart-offer-details');
    const summary = element('summary', 'nm-cart-offer-summary');
    summary.append(element('strong', '', breechIds.has(Number(source.product_id)) ? labels.offerBreech : labels.offerShirt), element('span', '', labels.offerChooseButton));
    const content = element('div', 'nm-cart-offer-content');
    const current = element('p', 'nm-cart-offer-current', labels.offerCurrent.replace('__ITEM__', `${source.product_title} · ${source.variant_title}`));
    const note = element('p', 'nm-cart-offer-help', labels.offerKeepItem);
    const mount = element('div', 'nm-cart-offer-widget');
    const message = element('p', 'nm-cart-offer-message', labels.offerLoading);
    message.setAttribute('role', 'status');
    const add = button(labels.offerAdd, 'nm-cart-offer-add', 'complete-bundle', source.key);
    add.disabled = true;
    add.dataset.unavailable = 'true';
    content.append(current, note, mount, message, add);
    details.append(summary, content);
    node.append(details);
    node.hidden = false;
    const state = { source, node, details, mount, message, add, initialized: false };
    offerState = state;
    const update = () => {
      if (offerState !== state) return;
      const widget = mount.querySelector('kaching-bundles-block');
      if (widget && !widget.dataset.nmCartOfferBound) {
        widget.dataset.nmCartOfferBound = 'true';
        widget.setAttribute('current-variant-id', String(source.variant_id || source.id));
        widget.setAttribute('compact', '');
        widget.addEventListener('items-changed', update);
        widget.addEventListener('kaching-bundles-block-loaded', update);
      }
      const selection = offerSelection(state);
      const ready = Boolean(selection.added);
      add.dataset.unavailable = String(!ready);
      add.disabled = Boolean(busyCount) || !ready;
      message.textContent = ready ? labels.offerSavingsNote : selection.message;
      if (widget && typeof widget.items === 'function') clearTimeout(state.timer);
    };
    details.addEventListener('toggle', async () => {
      if (!details.open || state.initialized) return;
      state.initialized = true;
      try {
        const publicConfig = JSON.parse(document.getElementById('kaching-bundles-config')?.textContent || '{}');
        if (!publicConfig.shopifyDomain || !publicConfig.storefrontAccessToken) throw new Error(labels.offerUnavailable);
        await loadBundleSdk();
        if (offerState !== state || !node.isConnected) return;
        const widget = element('kaching-bundle-component');
        widget.setAttribute('shop-domain', publicConfig.shopifyDomain);
        widget.setAttribute('storefront-token', publicConfig.storefrontAccessToken);
        widget.setAttribute('product-id', String(source.product_id));
        widget.setAttribute('selected-variant-id', String(source.variant_id || source.id));
        widget.setAttribute('deal-block-id', breechIds.has(Number(source.product_id)) ? offerSettings.breechDealId : offerSettings.shirtDealId);
        widget.setAttribute('country', config.country);
        widget.setAttribute('language', config.locale.split('-')[0]);
        widget.setAttribute('compact', '');
        widget.addEventListener('loaded', update);
        widget.addEventListener('items-changed', update);
      state.observer = new MutationObserver(update);
      state.observer.observe(mount, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'aria-selected', 'aria-checked'] });
      mount.append(widget);
      state.timer = setTimeout(() => {
        if (offerState !== state || mount.querySelector('kaching-bundles-block')) return;
        state.observer?.disconnect();
        mount.replaceChildren();
        state.initialized = false;
        message.textContent = labels.offerUnavailable;
      }, 15000);
      update();
      } catch {
        if (offerState !== state) return;
        state.observer?.disconnect();
        clearTimeout(state.timer);
        mount.replaceChildren();
        message.textContent = labels.offerUnavailable;
        state.initialized = false;
      }
    });
  };
  const completeBundle = state => {
    const selected = offerSelection(state);
    if (!selected.added) { state.message.textContent = selected.message; return Promise.resolve(); }
    const original = cart.items.find(item => item.key === state.source.key);
    if (!original || !offerEligible(original)) return Promise.reject(new Error(labels.offerChanged));
    const fingerprint = JSON.stringify([original.key, selected.original, selected.added]);
    return enqueue(`bundle:${fingerprint}`, async () => {
      ++requestVersion;
      const current = cart.items.find(item => item.key === original.key);
      if (!current || !offerEligible(current)) throw new Error(labels.offerChanged);
      const mergedProperties = { ...(current.properties || {}), ...selected.original.properties };
      const originalSignature = signature(current);
      const addedSignature = signature(selected.added);
      if (cart.items.some(item => signature(item) === addedSignature)) throw new Error(labels.offerChanged);
      let addedResponse;
      let latest;
      let tagged = false;
      try {
        addedResponse = await post('cart/add.js', { items: [selected.added] });
        latest = await api('cart.js');
        const originals = latest.items.filter(item => signature(item) === originalSignature);
        if (originals.length !== 1 || originals[0].quantity !== 1) throw new Error(labels.offerChanged);
        const updated = await post('cart/change.js', { id: originals[0].key, quantity: 1, properties: mergedProperties });
        tagged = true;
        render(updated);
        showMessage(labels.added);
        const pairSignatures = new Map();
        [signature({ ...current, properties: mergedProperties }), addedSignature].forEach(value => pairSignatures.set(value, (pairSignatures.get(value) || 0) + 1));
        const confirmed = [...pairSignatures].every(([value, quantity]) => {
          const lines = updated.items.filter(item => signature(item) === value);
          return lines.reduce((sum, item) => sum + item.quantity, 0) === quantity && lines.every(item => Number(item.original_line_price) > 0 && Number(item.final_line_price) <= Math.round(Number(item.original_line_price) * 0.8) + 1);
        });
        if (!confirmed) showMessage(labels.offerDiscountCheck, true);
        return updated;
      } catch (error) {
        if (tagged) {
          const failure = new Error(labels.addedRefreshError);
          failure.cartWasModified = true;
          throw failure;
        }
        if (addedResponse) {
          try {
            if (!latest) latest = await api('cart.js');
            const addedLines = latest.items.filter(item => signature(item) === addedSignature);
            if (addedLines.length !== 1 || addedLines[0].quantity !== 1) throw new Error(labels.offerRollbackError);
            const restored = await post('cart/change.js', { id: addedLines[0].key, quantity: 0 });
            render(restored);
          } catch {
            try { await refresh(); } catch {}
            throw new Error(labels.offerRollbackError);
          }
        }
        throw error;
      }
    });
  };

  const makeEmpty = () => {
    const empty = element('div', 'nm-cart-empty');
    empty.append(element('h3', '', labels.empty), element('p', '', labels.emptyHelp));
    const shop = element('a', 'nm-cart-shop', labels.shop);
    shop.href = config.shopUrl;
    empty.append(shop);
    return empty;
  };
  const makeLine = (item, index, prefix) => {
    const line = element('article', 'nm-cart-item');
    line.dataset.nmCartLine = '';
    line.dataset.key = item.key;
    line.dataset.variantId = item.variant_id || item.id;
    const productUrl = safeUrl(item.url, config.shopUrl, true);
    const imageLink = element('a', 'nm-cart-item-image');
    imageLink.href = productUrl;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');
    const source = item.featured_image?.url || item.image;
    if (source) {
      const image = element('img');
      const imageUrl = new URL(safeUrl(source), location.origin);
      imageUrl.searchParams.set('width', '240');
      image.src = imageUrl.href;
      image.alt = item.featured_image?.alt || item.product_title;
      image.width = 120;
      image.height = 150;
      image.loading = 'lazy';
      imageLink.append(image);
    }
    const content = element('div', 'nm-cart-item-content');
    const title = element('a', 'nm-cart-item-title', item.product_title || item.title);
    title.href = productUrl;
    content.append(title);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const options = element('dl', 'nm-cart-item-options');
      const values = item.options_with_values || [];
      if (values.length) values.forEach(option => {
        const row = element('div');
        row.append(element('dt', '', `${option.name}:`), element('dd', '', option.value));
        options.append(row);
      });
      else options.append(element('dd', '', item.variant_title));
      content.append(options);
    }
    Object.entries(item.properties || {}).forEach(([name, value]) => {
      if (!name.startsWith('_') && value !== '' && value !== null) content.append(element('p', 'nm-cart-item-property', `${name}: ${value}`));
    });
    if (item.selling_plan_allocation?.selling_plan?.name) content.append(element('p', 'nm-cart-item-property', item.selling_plan_allocation.selling_plan.name));
    const price = element('div', 'nm-cart-item-price');
    price.append(element('strong', '', money(item.final_line_price)));
    if (item.original_line_price > item.final_line_price) price.append(element('s', '', money(item.original_line_price)));
    content.append(price);
    if (item.quantity > 1) content.append(element('p', 'nm-cart-item-property', `${money(item.final_price)} ${labels.unitPrice}`));
    if (item.line_level_discount_allocations?.length) {
      const discounts = element('ul', 'nm-cart-item-discounts');
      discounts.setAttribute('role', 'list');
      item.line_level_discount_allocations.forEach(allocation => discounts.append(element('li', '', `${allocation.discount_application.title} (−${money(allocation.amount)})`)));
      content.append(discounts);
    }
    const controls = element('div', 'nm-cart-item-controls');
    const quantity = element('div', 'nm-cart-quantity');
    const decrease = button('−', 'nm-cart-quantity-button', 'decrease', item.key);
    decrease.setAttribute('aria-label', `${labels.decrease}: ${item.product_title}`);
    const input = element('input', 'nm-cart-quantity-input');
    input.type = 'number';
    input.min = '0';
    input.step = '1';
    input.value = item.quantity;
    input.inputMode = 'numeric';
    input.name = 'updates[]';
    input.id = `nm-cart-qty-${prefix}-${index}`;
    input.dataset.nmCartQuantity = '';
    input.dataset.key = item.key;
    input.setAttribute('aria-label', `${labels.quantity}: ${item.product_title}`);
    const increase = button('+', 'nm-cart-quantity-button', 'increase', item.key);
    increase.setAttribute('aria-label', `${labels.increase}: ${item.product_title}`);
    quantity.append(decrease, input, increase);
    controls.append(quantity);
    if (item.variant_title && item.variant_title !== 'Default Title') {
      const edit = button(labels.edit, 'nm-cart-text-button', 'edit', item.key);
      edit.setAttribute('aria-label', `${labels.edit}: ${item.product_title}`);
      edit.setAttribute('aria-expanded', 'false');
      controls.append(edit);
    } else {
      const view = element('a', 'nm-cart-item-view', labels.view);
      view.href = productUrl;
      controls.append(view);
    }
    const remove = button(labels.remove, 'nm-cart-text-button', 'remove', item.key);
    remove.setAttribute('aria-label', `${labels.remove}: ${item.product_title}`);
    controls.append(remove);
    content.append(controls);
    line.append(imageLink, content);
    return line;
  };
  const discountRows = () => {
    const totals = new Map();
    (cart.items || []).forEach(item => (item.line_level_discount_allocations || []).forEach(allocation => {
      const title = allocation.discount_application.title;
      totals.set(title, (totals.get(title) || 0) + Number(allocation.amount || 0));
    }));
    (cart.cart_level_discount_applications || []).forEach(discount => totals.set(discount.title, (totals.get(discount.title) || 0) + Number(discount.total_allocated_amount || 0)));
    const sum = [...totals.values()].reduce((total, amount) => total + amount, 0);
    if (sum !== cart.total_discount || !totals.size) return [[labels.savings, cart.total_discount]];
    return [...totals.entries()];
  };
  const makeTotals = () => {
    const totals = document.createDocumentFragment();
    const row = (title, value, className = '') => {
      const node = element('div', `nm-cart-total-row ${className}`);
      node.append(element('span', '', title), element('span', '', value));
      totals.append(node);
    };
    row(labels.items, money(cart.original_total_price));
    if (cart.total_discount > 0) discountRows().forEach(([title, amount]) => { if (amount > 0) row(title, `−${money(amount)}`, 'nm-cart-savings'); });
    row(labels.subtotal, money(cart.total_price, true), 'nm-cart-subtotal');
    return totals;
  };
  const renderShipping = node => {
    node.replaceChildren();
    // Use the latest cart currency and physical lines. A cached cart-level flag
    // or the page's original currency must not hide the first item's progress.
    const threshold = Number(config.shippingThresholds
      ? (['CA', 'US'].includes(config.country) ? config.shippingThresholds[cart.currency] || 0 : 0)
      : (cart.currency === config.currency ? config.threshold : 0));
    const physicalItems = cart.items.filter(item => item.requires_shipping);
    if (!cart.item_count || !physicalItems.length || !Number.isFinite(threshold) || threshold <= 0) {
      node.hidden = true;
      return;
    }
    const physicalTotal = physicalItems.reduce((sum, item) => sum + Number(item.final_line_price), 0);
    const total = Math.min(cart.total_price, physicalTotal);
    const remaining = Math.max(0, threshold - total);
    if (labels.shippingOffer) node.append(element('p', 'nm-cart-shipping-heading', labels.shippingOffer.replace('__AMOUNT__', money(threshold, true))));
    node.append(element('p', '', remaining ? labels.shippingAway.replace('__AMOUNT__', money(remaining)) : labels.shippingReached));
    const progress = element('progress', 'nm-cart-shipping-progress');
    progress.max = threshold;
    progress.value = Math.min(total, threshold);
    progress.setAttribute('aria-label', labels.shippingProgress);
    node.append(progress, element('p', 'nm-cart-shipping-terms', labels.shippingTerms));
    node.hidden = false;
  };
  const render = data => {
    // A committed add/change must invalidate any older cart read still in flight.
    ++requestVersion;
    cart = { ...data, currency: typeof data.currency === 'string' ? data.currency : data.currency?.iso_code || config.currency };
    const focusState = captureFocus();
    surfaces().forEach((surface, surfaceIndex) => {
      const list = surface.querySelector('[data-nm-cart-items]');
      if (list) {
        const items = document.createDocumentFragment();
        if (!cart.items.length) items.append(makeEmpty());
        else cart.items.forEach((item, index) => items.append(makeLine(item, index, surfaceIndex)));
        list.replaceChildren(items);
      }
      surface.querySelector('[data-nm-cart-totals]')?.replaceChildren(makeTotals());
      const footer = surface.querySelector('[data-nm-cart-footer]');
      if (footer) footer.hidden = !cart.item_count;
      const tax = surface.querySelector('[data-nm-cart-tax]');
      if (tax) tax.textContent = cart.taxes_included ? labels.taxIncluded : labels.taxCheckout;
      surface.querySelectorAll('[data-nm-cart-shipping]').forEach(renderShipping);
      surface.querySelectorAll('[data-nm-cart-offer]').forEach(renderOffer);
    });
    document.querySelectorAll('[data-nm-cart-count]').forEach(node => { node.textContent = cart.item_count; });
    setBusy();
    window.ARMATEQBNPL?.update(cart.total_price, 'cart', cart);
    if (!busyCount) restoreFocus(focusState);
    document.dispatchEvent(new CustomEvent('armateq:cart-updated', { detail: { cart } }));
  };
  const refresh = async () => {
    const version = ++requestVersion;
    const data = await api('cart.js');
    if (version === requestVersion) render(data);
    return data;
  };
  const open = (options = {}) => {
    drawer = document.querySelector('[data-nm-cart-dialog]');
    if (!drawer) { location.href = config.cartUrl; return; }
    if (!drawer.open) {
      opener = options.opener || document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
      drawer.showModal();
      document.body.style.overflow = 'hidden';
      drawer.querySelector('[data-nm-cart-close]')?.focus({ preventScroll: true });
    }
    if (options.added) showMessage(labels.added);
    else if (!busyCount) showMessage();
    if (options.refresh !== false) refresh().catch(() => showMessage(labels.refreshError, true));
  };
  const close = () => drawer?.close();
  const enqueue = (key, operation) => {
    if (pending.has(key)) return pending.get(key);
    const task = queue.catch(() => {}).then(async () => {
      mutationFocus = captureFocus();
      busyCount += 1;
      setBusy();
      clearMessages();
      try { return await operation(); }
      catch (error) { showMessage(error.message || labels.error, true); throw error; }
      finally { busyCount -= 1; setBusy(); restoreFocus(mutationFocus); mutationFocus = null; }
    });
    pending.set(key, task);
    queue = task;
    task.finally(() => pending.delete(key)).catch(() => {});
    return task;
  };
  const addItems = items => {
    const addOpener = document.activeElement;
    const form = items instanceof FormData;
    const payload = form ? items : { items: Array.isArray(items) ? items : items.items };
    if (!form && (!Array.isArray(payload.items) || !payload.items.length)) return Promise.reject(new Error(labels.error));
    const fingerprint = form ? JSON.stringify([...items.entries()].map(([key, value]) => [key, typeof value === 'string' ? value : value.name])) : JSON.stringify(payload);
    return enqueue(`add:${fingerprint}`, async () => {
      const added = form ? await api('cart/add.js', { method: 'POST', body: payload }) : await post('cart/add.js', payload);
      try {
        const updated = await refresh();
        open({ added: true, refresh: false, opener: addOpener });
        return updated;
      } catch {
        open({ added: true, refresh: false, opener: addOpener });
        const error = new Error(labels.addedRefreshError);
        error.cartWasModified = true;
        error.addedItems = added.items || [added];
        throw error;
      }
    });
  };
  const change = (key, quantity) => enqueue(`change:${key}:${quantity}`, async () => {
    if (!Number.isInteger(quantity) || quantity < 0) throw new Error(labels.error);
    ++requestVersion;
    const updated = await post('cart/change.js', { id: key, quantity });
    render(updated);
    showMessage(labels.updated);
    return updated;
  });
  const signature = item => JSON.stringify({
    id: Number(item.variant_id || item.id),
    properties: Object.fromEntries(Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))),
    sellingPlan: item.selling_plan_allocation?.selling_plan?.id || null
  });
  const replaceVariant = (key, variantId) => enqueue(`replace:${key}:${variantId}`, async () => {
    const original = cart.items.find(item => item.key === key);
    if (!original) throw new Error(labels.error);
    if (Number(original.variant_id || original.id) === Number(variantId)) return cart;
    const replacement = { id: Number(variantId), quantity: original.quantity, properties: { ...(original.properties || {}) } };
    const sellingPlan = original.selling_plan_allocation?.selling_plan?.id;
    if (sellingPlan) replacement.selling_plan = sellingPlan;
    await post('cart/add.js', { items: [replacement] });
    let updated;
    try {
      updated = await api('cart.js');
      const originalSignature = signature(original);
      const matches = updated.items.filter(item => signature(item) === originalSignature);
      const currentOriginal = updated.items.find(item => item.key === key) || (matches.length === 1 ? matches[0] : null);
      if (!currentOriginal || currentOriginal.quantity !== original.quantity) throw new Error(labels.editPartialError);
      updated = await post('cart/change.js', { id: currentOriginal.key, quantity: 0 });
    } catch {
      try { await refresh(); } catch { if (updated) render(updated); }
      const error = new Error(labels.editPartialError);
      error.cartWasModified = true;
      throw error;
    }
    ++requestVersion;
    render(updated);
    showMessage(labels.updated);
    return updated;
  });
  const editLine = async (key, trigger) => {
    const line = trigger.closest('[data-nm-cart-line]');
    const existing = line.querySelector('[data-nm-cart-editor]');
    if (existing) {
      existing.remove();
      trigger.setAttribute('aria-expanded', 'false');
      return;
    }
    const item = cart.items.find(entry => entry.key === key);
    if (!item) return;
    trigger.disabled = true;
    clearMessages();
    try {
      const productUrl = new URL(safeUrl(item.url, config.shopUrl, true));
      productUrl.search = '';
      productUrl.hash = '';
      productUrl.pathname += '.js';
      let product = products.get(productUrl.href);
      if (!product) {
        const response = await fetch(productUrl.href, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(labels.error);
        product = await response.json();
        products.set(productUrl.href, product);
      }
      if (!line.isConnected) return;
      const editor = element('div', 'nm-cart-editor');
      editor.dataset.nmCartEditor = '';
      const label = element('label', 'nm-cart-edit-label', labels.editOptions);
      const select = element('select', 'nm-cart-edit-select');
      select.id = `nm-edit-${crypto.randomUUID()}`;
      select.dataset.nmCartEditSelect = '';
      label.htmlFor = select.id;
      product.variants.forEach(variant => {
        const option = element('option', '', `${variant.title} — ${money(variant.price)}${variant.available ? '' : ` · ${labels.soldOut}`}`);
        option.value = variant.id;
        option.disabled = !variant.available && Number(variant.id) !== Number(item.variant_id || item.id);
        option.selected = Number(variant.id) === Number(item.variant_id || item.id);
        select.append(option);
      });
      const save = button(labels.saveOptions, 'nm-cart-save-options', 'save-options', key);
      const cancel = button(labels.cancel, 'nm-cart-text-button', 'cancel-edit', key);
      const note = element('p', 'nm-cart-item-property', labels.editPriceNote);
      const actions = element('div', 'nm-cart-editor-actions');
      actions.append(save, cancel);
      editor.append(label, select, note, actions);
      line.querySelector('.nm-cart-item-content').append(editor);
      trigger.setAttribute('aria-expanded', 'true');
      select.focus();
    } catch (error) { showMessage(error.message || labels.error, true); }
    finally { trigger.disabled = false; }
  };

  document.addEventListener('click', event => {
    const closeButton = event.target.closest('[data-nm-cart-close]');
    if (closeButton) { event.preventDefault(); close(); return; }
    const openButton = event.target.closest('[data-nm-cart-open]');
    if (openButton) { event.preventDefault(); clearMessages(); open(); return; }
    const action = event.target.closest('[data-nm-cart-action], [data-nm-cart-remove]');
    if (!action || busyCount) return;
    event.preventDefault();
    const key = action.dataset.key;
    if (action.dataset.nmCartAction === 'complete-bundle') {
      if (offerState) completeBundle(offerState).then(() => {
        if (!action.isConnected) drawer?.querySelector('[data-nm-cart-checkout]')?.focus({ preventScroll: true });
      }).catch(error => {
        showMessage(error.message || labels.error, true);
        if (!action.isConnected) drawer?.querySelector('[data-nm-cart-error]')?.setAttribute('tabindex', '-1');
        if (!action.isConnected) drawer?.querySelector('[data-nm-cart-error]')?.focus({ preventScroll: true });
      });
      return;
    }
    const item = cart?.items.find(entry => entry.key === key);
    if (!item) return;
    const actionName = action.dataset.nmCartAction || 'remove';
    if (actionName === 'edit') { editLine(key, action); return; }
    if (actionName === 'cancel-edit') {
      const line = action.closest('[data-nm-cart-line]');
      line.querySelector('[data-nm-cart-editor]')?.remove();
      const edit = line.querySelector('[data-nm-cart-action="edit"]');
      edit?.setAttribute('aria-expanded', 'false');
      edit?.focus();
      return;
    }
    if (actionName === 'save-options') {
      const selected = action.closest('[data-nm-cart-editor]').querySelector('select').value;
      replaceVariant(key, selected).catch(() => {});
      return;
    }
    const quantity = actionName === 'remove' ? 0 : item.quantity + (actionName === 'increase' ? 1 : -1);
    change(key, quantity).catch(() => {});
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-nm-cart-quantity]');
    if (!input || busyCount) return;
    const value = Number(input.value);
    const item = cart?.items.find(entry => entry.key === input.dataset.key);
    if (!item) return;
    if (!Number.isInteger(value) || value < 0) { input.value = item.quantity; return; }
    if (value !== item.quantity) change(item.key, value).catch(() => { input.value = item.quantity; });
  });
  document.addEventListener('armateq:cart-open', event => open(event.detail || {}));
  document.addEventListener('armateq:cart-refresh', () => refresh().catch(() => showMessage(labels.refreshError, true)));
  drawer?.addEventListener('click', event => { if (event.target === drawer) close(); });
  drawer?.addEventListener('close', () => {
    closeOfferChooser(false);
    document.body.style.overflow = previousBodyOverflow;
    if (opener?.isConnected) opener.focus({ preventScroll: true });
  });
  document.addEventListener('shopify:section:load', () => { if (cart) render(cart); });
  window.addEventListener('pageshow', event => { if (event.persisted) refresh().catch(() => {}); });
  window.ARMATEQCart = { loadBundleSdk, addItems, addFormData: addItems, open, close, refresh, change, getCart: () => cart };
  document.documentElement.dataset.nmCartReady = 'true';
  try {
    const seed = JSON.parse(document.getElementById('nm-cart-seed').textContent);
    if (seed && Array.isArray(seed.items)) render(seed);
  } catch { refresh().catch(() => {}); }
})();
